/**
 * CreateTenantDialog — "Create tenant" dialog on the Tenants screen
 * (WSRPDM-0018, contract §5; re-pointed to wsr-api in WSRPDM-0083). Fields:
 * `slug` (required, DNS label, ≤63, client-validated), `name` (required,
 * ≤200), `signupMode` (required select of the 4 values). This component only
 * owns the form and the create call; on success the parent closes the dialog,
 * refreshes both rosters, and shows the success banner.
 *
 * `createTenant` goes through wsr-api, so errors arrive in its flat
 * `{code,message,status}` body — read via `wsrApiError()`, never
 * `ApiError.code` (which normalizes to `UNKNOWN` for that shape). A create can
 * take up to ~30 s: while it is in flight Create stays disabled, Cancel and
 * Esc/backdrop cannot close the dialog, and a long-running hint shows.
 * `TENANT_CREATE_OUTCOME_UNKNOWN` keeps the typed values, shows a warning and
 * asks the parent (`onOutcomeUnknown`) to refresh — never an auto-retry.
 */
import { useRef, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { createTenant } from '@/api/saas/tenants';
import { wsrApiError } from '@/api/wsr/errors';
import type { SignupMode, TenantDto } from '@/api/saas/types';

const SIGNUP_MODES: SignupMode[] = ['PUBLIC', 'INVITE_ONLY', 'ADMIN_CREATED_ONLY', 'DISABLED'];

/** DNS label: lowercase letters/digits, hyphens not at either end, ≤63 chars. */
const SLUG_PATTERN = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;

const SLUG_TAKEN_CODES = new Set([
  'HOSTNAME_ALREADY_REGISTERED',
  'TENANT_SLUG_TAKEN',
  'SLUG_TAKEN_BY_PRODUCT',
]);
const UPSTREAM_CODES = new Set(['UPSTREAM_MISCONFIGURED', 'UPSTREAM_UNAVAILABLE']);

interface CreateTenantDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (tenant: TenantDto) => void;
  /** `TENANT_CREATE_OUTCOME_UNKNOWN` — the parent refreshes its rosters; the dialog stays open. */
  onOutcomeUnknown: () => void;
}

interface FormState {
  slug: string;
  name: string;
  signupMode: SignupMode;
}

const INITIAL_FORM: FormState = { slug: '', name: '', signupMode: 'INVITE_ONLY' };

export function CreateTenantDialog({
  open,
  onClose,
  onCreated,
  onOutcomeUnknown,
}: CreateTenantDialogProps) {
  const { t } = useCopy();
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [slugError, setSlugError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [outcomeUnknown, setOutcomeUnknown] = useState<string | null>(null);
  // Guards a second activation that lands before the disabled state renders:
  // one activation = one POST.
  const inFlight = useRef(false);
  const slugRef = useRef<HTMLInputElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  const resetAndClose = () => {
    setForm(INITIAL_FORM);
    setSlugError(null);
    setNameError(null);
    setFormError(null);
    setOutcomeUnknown(null);
    onClose();
  };

  const handleDialogClose = () => {
    // An in-flight create cannot be aborted, so Esc/backdrop are ignored until it settles.
    if (submitting) return;
    resetAndClose();
  };

  /** Returns the first field in error (Slug before Name), or null when the form is valid. */
  const validate = (): 'slug' | 'name' | null => {
    let firstError: 'slug' | 'name' | null = null;
    if (form.slug.trim() === '') {
      setSlugError(t('tenants.create.slugRequired', 'Slug is required.'));
      firstError = 'slug';
    } else if (!SLUG_PATTERN.test(form.slug.trim())) {
      setSlugError(
        t(
          'tenants.create.slugInvalid',
          'Slug must be a valid DNS label (lowercase letters, numbers, hyphens; up to 63 characters).',
        ),
      );
      firstError = 'slug';
    } else {
      setSlugError(null);
    }
    if (form.name.trim() === '') {
      setNameError(t('tenants.create.nameRequired', 'Name is required.'));
      firstError ??= 'name';
    } else if (form.name.trim().length > 200) {
      setNameError(t('tenants.create.nameTooLong', 'Name must be 200 characters or fewer.'));
      firstError ??= 'name';
    } else {
      setNameError(null);
    }
    return firstError;
  };

  const handleSubmit = async () => {
    if (inFlight.current) return;
    setFormError(null);
    setOutcomeUnknown(null);
    const invalidField = validate();
    if (invalidField) {
      (invalidField === 'slug' ? slugRef : nameRef).current?.focus();
      return;
    }
    const slug = form.slug.trim();
    inFlight.current = true;
    setSubmitting(true);
    try {
      const tenant = await createTenant({
        slug,
        name: form.name.trim(),
        signupMode: form.signupMode,
      });
      setForm(INITIAL_FORM);
      onCreated(tenant);
    } catch (err) {
      const body = wsrApiError(err);
      const serverMessage =
        typeof body?.message === 'string' && body.message.trim() !== '' ? body.message : null;
      const code = body?.code;
      if (code === 'RESERVED_SLUG') {
        setSlugError(
          serverMessage ??
            t('tenants.create.errorReservedSlug', 'That slug is reserved. Choose a different one.'),
        );
        slugRef.current?.focus();
      } else if (code !== undefined && SLUG_TAKEN_CODES.has(code)) {
        setSlugError(
          serverMessage ??
            t(
              'tenants.create.errorDuplicateSlug',
              'That slug is already registered. Choose a different one.',
            ),
        );
        slugRef.current?.focus();
      } else if (code === 'TENANT_CREATE_OUTCOME_UNKNOWN') {
        setOutcomeUnknown(
          t(
            'tenants.create.errorOutcomeUnknown',
            "We couldn't confirm whether the tenant was created. The tenant list has been refreshed — check it for “{{slug}}” before trying again.",
            { slug },
          ),
        );
        onOutcomeUnknown();
      } else if (code !== undefined && UPSTREAM_CODES.has(code)) {
        setFormError(
          t('tenants.create.errorGeneric', 'Could not create tenant. Please try again.'),
        );
      } else {
        setFormError(
          serverMessage ??
            t('tenants.create.errorGeneric', 'Could not create tenant. Please try again.'),
        );
      }
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('tenants.create.title', 'Create tenant')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {formError ? <Alert severity="error">{formError}</Alert> : null}
          {outcomeUnknown ? <Alert severity="warning">{outcomeUnknown}</Alert> : null}
          <TextField
            label={t('tenants.create.slugLabel', 'Slug')}
            helperText={
              slugError ??
              t(
                'tenants.create.slugHelper',
                'Lowercase letters, numbers, hyphens — up to 63 characters.',
              )
            }
            error={Boolean(slugError)}
            value={form.slug}
            onChange={(e) => setForm((prev) => ({ ...prev, slug: e.target.value }))}
            inputRef={slugRef}
            size="small"
            fullWidth
          />
          <TextField
            label={t('tenants.create.nameLabel', 'Name')}
            helperText={nameError ?? undefined}
            error={Boolean(nameError)}
            value={form.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            inputRef={nameRef}
            size="small"
            fullWidth
          />
          <TextField
            select
            label={t('tenants.create.signupModeLabel', 'Signup mode')}
            value={form.signupMode}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, signupMode: e.target.value as SignupMode }))
            }
            size="small"
            fullWidth
          >
            {SIGNUP_MODES.map((mode) => (
              <MenuItem key={mode} value={mode}>
                {t(`tenants.signupMode.${mode}`, mode)}
              </MenuItem>
            ))}
          </TextField>
          {submitting ? (
            <Typography variant="body2" color="text.secondary" role="status">
              {t('tenants.create.longRunningHint', 'Creating a tenant can take up to 30 seconds.')}
            </Typography>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={resetAndClose} disabled={submitting}>
          {t('tenants.create.cancelButton', 'Cancel')}
        </Button>
        <Button onClick={() => void handleSubmit()} variant="contained" disabled={submitting}>
          {submitting
            ? t('tenants.create.submitting', 'Creating…')
            : t('tenants.create.submitButton', 'Create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
